import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { handleToast } from '../../Toast';
import { useActivesWallet } from '../../../jsx/pages/Wallet/hooks/ActivesHooks';
import { InternationalTradeForm } from '../InternationalTradeForm';

const apiUrl = process.env.REACT_APP_API_URL;
const TRADES_URL = '/api/international/trades/';

const fmtUsd = (v) =>
  v == null
    ? '-'
    : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * Gestão de operações (trades) de um ativo internacional:
 * form de compra/venda + histórico com editar/excluir. Usado nas páginas
 * EtfActives / StocksActives / ReitsActives.
 */
export function InternationalTradeManager({ active, onClose }) {
  const { updateWallet } = useActivesWallet();
  const ticker = String(active?.tickerSymbol || '').toUpperCase();

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);

  const loadTrades = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}${TRADES_URL}`, {
        params: { ticker },
      });
      const data = res.data?.results ?? res.data ?? [];
      setTrades(Array.isArray(data) ? data : []);
    } catch (error) {
      setTrades([]);
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  async function handleDelete(trade) {
    try {
      await axios.delete(`${apiUrl}${TRADES_URL}${trade.id}/`);
      handleToast(false, 'Operação excluída com sucesso');
      if (editingTrade?.id === trade.id) setEditingTrade(null);
      updateWallet();
      loadTrades();
    } catch (error) {
      handleToast(true, 'Erro ao excluir a operação');
    }
  }

  function handleSaved() {
    setEditingTrade(null);
    loadTrades();
  }

  return (
    <div>
      <InternationalTradeForm
        key={editingTrade?.id || 'new'}
        ticker={ticker}
        trade={editingTrade}
        onCancel={editingTrade ? () => setEditingTrade(null) : undefined}
        onSaved={handleSaved}
      />

      <div style={{ marginTop: '1.5rem' }}>
        <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
          Operações registradas
        </strong>
        {loading ? (
          <p>Carregando...</p>
        ) : trades.length === 0 ? (
          <p style={{ color: '#6A717E' }}>Nenhuma operação registrada.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6A717E' }}>
                <th style={{ padding: '4px 6px' }}>Data</th>
                <th style={{ padding: '4px 6px' }}>Lado</th>
                <th style={{ padding: '4px 6px' }}>Qtd.</th>
                <th style={{ padding: '4px 6px' }}>Preço</th>
                <th style={{ padding: '4px 6px' }}>Corretora</th>
                <th style={{ padding: '4px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {trades.map((t) => (
                <tr key={t.id} style={{ borderTop: '1px solid #ECF0F3' }}>
                  <td style={{ padding: '6px' }}>{t.trade_date}</td>
                  <td style={{ padding: '6px' }}>
                    <span style={{ color: t.side === 'BUY' ? '#1FA75B' : '#EC6643' }}>
                      {t.side === 'BUY' ? 'Compra' : 'Venda'}
                    </span>
                  </td>
                  <td style={{ padding: '6px' }}>{t.quantity}</td>
                  <td style={{ padding: '6px' }}>{fmtUsd(t.price_usd)}</td>
                  <td style={{ padding: '6px' }}>{t.broker_origin}</td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
                    <PencilSimple
                      size={20}
                      color="#303F4B"
                      style={{ cursor: 'pointer', marginRight: 10 }}
                      onClick={() => setEditingTrade(t)}
                    />
                    <Trash
                      size={20}
                      color="#EC6643"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleDelete(t)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {onClose && (
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-light btn-block"
            onClick={onClose}
          >
            Fechar
          </button>
        </div>
      )}
    </div>
  );
}
